import * as React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowUpRight,
  BadgeCheck,
  Clock,
  CreditCard,
  Layers,
  PawPrint,
  RefreshCw,
  Store,
  TrendingUp,
  Unlock,
  Users,
} from "lucide-react";
import { api } from "@/lib/api";
import { cn, formatMoney, formatNumber } from "@/lib/utils";
import { PageHeader } from "@/components/data/toolbar";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton,
} from "@/components/ui/primitives";

interface Kpis {
  total_users: number;
  buyers: number;
  breeders: number;
  verified_breeders: number;
  signups_7d: number;
  dau: number;
  mau: number;
  listings_available: number;
  listings_sold: number;
  listings_removed: number;
  pending_verifications: number;
  revenue_inr: number;
  active_subscriptions: number;
  unlocks_total: number;
  unlocks_7d: number;
  open_reports: number;
  conversion_pct: number;
}

const StatCard = ({
  label,
  value,
  hint,
  icon: Icon,
  tone = "default",
  to,
  loading,
}: {
  label: string;
  value: React.ReactNode;
  hint?: string;
  icon: React.ComponentType<{ className?: string }>;
  tone?: "default" | "success" | "warning" | "danger" | "purple";
  to?: string;
  loading?: boolean;
}) => {
  const toneClass = {
    default: "text-primary bg-primary/10",
    success: "text-success bg-success/10",
    warning: "text-warning bg-warning/10",
    danger: "text-destructive bg-destructive/10",
    purple: "text-indigo-500 bg-indigo-500/10",
  }[tone];

  const textTone = {
    default: "text-foreground",
    success: "text-success",
    warning: "text-warning",
    danger: "text-destructive",
    purple: "text-indigo-600 dark:text-indigo-400",
  }[tone];

  const body = (
    <Card
      className={cn(
        "h-full border-border transition-all duration-200",
        to && "hover:border-primary/50 hover:shadow-md cursor-pointer",
      )}
    >
      <CardContent className="flex items-start justify-between p-5">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
          {loading ? (
            <Skeleton className="mt-2 h-8 w-24" />
          ) : (
            <p className={cn("numeric mt-1 text-2xl font-bold tracking-tight", textTone)}>
              {value}
            </p>
          )}
          {hint && !loading && (
            <p className="mt-1.5 flex items-center gap-1 text-xs text-muted-foreground">
              {hint}
            </p>
          )}
        </div>
        <span className={cn("grid h-11 w-11 shrink-0 place-items-center rounded-xl", toneClass)}>
          <Icon className="h-5 w-5" />
        </span>
      </CardContent>
    </Card>
  );

  return to ? <Link to={to}>{body}</Link> : body;
};

export const DashboardPage = () => {
  const { data: k, isLoading, isFetching, refetch } = useQuery<Kpis>({
    queryKey: ["dashboard", "kpis"],
    queryFn: () => api.get<Kpis>("/dashboard/kpis"),
    refetchInterval: 30_000,
  });

  const verifiedBreederPct =
    k?.breeders && k.breeders > 0
      ? Math.round(((k.verified_breeders || 0) / k.breeders) * 100)
      : 0;

  const buyerPct =
    k?.total_users && k.total_users > 0
      ? Math.round(((k.buyers || 0) / k.total_users) * 100)
      : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <PageHeader
          title="Dashboard"
          description="Overview of GetPet users, breeders, verifications, and marketplace metrics."
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => void refetch()}
          loading={isFetching && !isLoading}
          className="self-start gap-1.5 sm:self-auto"
        >
          <RefreshCw className={cn("h-3.5 w-3.5", isFetching && "animate-spin")} />
          Refresh Stats
        </Button>
      </div>

      {/* Pending Verifications Action Banner */}
      {k?.pending_verifications != null && k.pending_verifications > 0 && (
        <Card className="border-warning/50 bg-warning/5">
          <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-warning/15 text-warning">
                <Clock className="h-5 w-5" />
              </span>
              <div>
                <p className="font-semibold text-foreground">
                  {k.pending_verifications} Breeder Verification Request{k.pending_verifications > 1 ? "s" : ""} Pending
                </p>
                <p className="text-xs text-muted-foreground">
                  Breeders have submitted identity &amp; pet shop documents requiring your review and approval.
                </p>
              </div>
            </div>
            <Button size="sm" asChild className="shrink-0 gap-1 font-medium">
              <Link to="/verification">
                Review Verifications <ArrowUpRight className="h-4 w-4" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Primary KPI Grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Total Users"
          value={formatNumber(k?.total_users ?? 0)}
          hint={`${formatNumber(k?.buyers ?? 0)} buyers (${buyerPct}%)`}
          icon={Users}
          tone="default"
          to="/users"
          loading={isLoading}
        />

        <StatCard
          label="Breeders"
          value={formatNumber(k?.breeders ?? 0)}
          hint={`${k?.verified_breeders ?? 0} verified (${verifiedBreederPct}%)`}
          icon={Store}
          tone="purple"
          to="/breeders"
          loading={isLoading}
        />

        <StatCard
          label="Pet Listings"
          value={formatNumber(k?.listings_available ?? 0)}
          hint={`${k?.listings_sold ?? 0} marked as sold`}
          icon={PawPrint}
          tone="success"
          to="/listings"
          loading={isLoading}
        />

        <StatCard
          label="Verifications"
          value={k?.pending_verifications ?? 0}
          hint={k?.pending_verifications ? "Requires administrator action" : "All requests up to date"}
          icon={BadgeCheck}
          tone={k?.pending_verifications ? "warning" : "success"}
          to="/verification"
          loading={isLoading}
        />
      </div>

      {/* Secondary Metrics Grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Unlocked Contacts"
          value={formatNumber(k?.unlocks_total ?? 0)}
          hint={`${k?.unlocks_7d ?? 0} unlocked in last 7 days`}
          icon={Unlock}
          tone="default"
          loading={isLoading}
        />

        <StatCard
          label="Platform Revenue"
          value={formatMoney(k?.revenue_inr ?? 0)}
          hint="From successful plan payments"
          icon={CreditCard}
          tone="success"
          loading={isLoading}
        />

        <StatCard
          label="Active Subscriptions"
          value={formatNumber(k?.active_subscriptions ?? 0)}
          hint="Active buyer &amp; breeder plans"
          icon={Layers}
          tone="purple"
          loading={isLoading}
        />

        <StatCard
          label="New Signups (7d)"
          value={formatNumber(k?.signups_7d ?? 0)}
          hint="Recent user registrations"
          icon={TrendingUp}
          tone="default"
          loading={isLoading}
        />
      </div>

      {/* Quick Navigation / Management Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card className="border-border hover:border-primary/40 transition-colors">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="h-4 w-4 text-primary" /> User Management
            </CardTitle>
            <CardDescription>
              View, search, filter, and moderate buyer and admin accounts.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" size="sm" asChild className="w-full justify-between">
              <Link to="/users">
                Go to Users Directory <ArrowUpRight className="h-4 w-4" />
              </Link>
            </Button>
          </CardContent>
        </Card>

        <Card className="border-border hover:border-primary/40 transition-colors">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Store className="h-4 w-4 text-indigo-500" /> Breeder Directory
            </CardTitle>
            <CardDescription>
              Manage verified breeders, review shop details, and toggle badges.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" size="sm" asChild className="w-full justify-between">
              <Link to="/breeders">
                Go to Breeders Directory <ArrowUpRight className="h-4 w-4" />
              </Link>
            </Button>
          </CardContent>
        </Card>

        <Card className="border-border hover:border-primary/40 transition-colors">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <BadgeCheck className="h-4 w-4 text-warning" /> Verification Queue
            </CardTitle>
            <CardDescription>
              Review uploaded KYC/Shop documents and approve or reject breeders.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" size="sm" asChild className="w-full justify-between">
              <Link to="/verification">
                Open Verification Queue <ArrowUpRight className="h-4 w-4" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Platform Health Overview */}
      <Card className="border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Marketplace Overview</CardTitle>
          <CardDescription>
            High-level distribution of platform participants and content.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="flex justify-between text-xs font-medium text-muted-foreground mb-1.5">
              <span>User Composition ({k?.buyers ?? 0} Buyers / {k?.breeders ?? 0} Breeders)</span>
              <span>{buyerPct}% Buyers</span>
            </div>
            <div className="h-2.5 w-full overflow-hidden rounded-full bg-surface">
              <div
                className="h-full rounded-full bg-primary transition-all duration-500"
                style={{ width: `${Math.max(5, buyerPct)}%` }}
              />
            </div>
          </div>

          <div>
            <div className="flex justify-between text-xs font-medium text-muted-foreground mb-1.5">
              <span>Breeder Verification Rate ({k?.verified_breeders ?? 0} of {k?.breeders ?? 0} verified)</span>
              <span>{verifiedBreederPct}% Verified</span>
            </div>
            <div className="h-2.5 w-full overflow-hidden rounded-full bg-surface">
              <div
                className="h-full rounded-full bg-success transition-all duration-500"
                style={{ width: `${Math.max(5, verifiedBreederPct)}%` }}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
